import { inject, makeEnvironmentProviders, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { GoogleTagManagerConfig, GoogleTagManagerService, provideGoogleTagManager } from 'angular-google-tag-manager';

import { AnalyticsService, PageViewService } from './services';

/**
 * THE single place GTM is loaded.
 *
 * Runs during application startup as an app initializer, so the container is in
 * the DOM and `window.dataLayer` is live before the app renders or any
 * analytics event can fire — on EVERY page, regardless of whether that page
 * ever emits an e-commerce/analytics event.
 *
 * Browser-only: `addGtmToDom()` touches `document`/`window`, so it must not run
 * during SSR. It is also idempotent (the library guards a private `isLoaded`
 * flag and a fixed `#GTMscript` element id), so it cannot double-inject.
 *
 * Do not move GTM loading out of this initializer (e.g. back into
 * `AnalyticsService.push()` via `pushTag()`). Doing so reintroduces lazy-loading
 * and the GA4 visibility loss it caused on event-less pages. That warning is
 * unchanged by the fire-and-forget behaviour below: GTM is still loaded from
 * here, at bootstrap, on every page. Only its FAILURE stopped being fatal.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS FIRE-AND-FORGET AND MUST STAY THAT WAY (BBS-0070)
 * ---------------------------------------------------------------------------
 *
 * This function used to `return addGtmToDom()`. Returning that promise from an
 * app initializer made Google Tag Manager — a third party we do not control and
 * which is blocked for a meaningful share of visitors — able to kill the entire
 * application:
 *
 *   - `addGtmToDom()` rejects on the script's `error` event. Angular's
 *     `ApplicationInitStatus` funnels initializer promises through
 *     `Promise.all`, and `appRef.bootstrap(rootComponent)` runs only inside
 *     `donePromise.then(...)` — so one rejection means the root component is
 *     never bootstrapped and Angular never hydrates.
 *   - On an SSR build the consequence is invisible and total: the
 *     server-rendered HTML stays on screen looking perfectly healthy while
 *     every `(click)` handler on the site is dead forever. Plain `<a href>`
 *     links keep working because the browser navigates them natively, which is
 *     exactly why this presented as "links work, but the menu won't open".
 *   - Anyone running a content blocker, a DNS filter, a VPN, or a corporate
 *     MDM profile hits this. Those users are also, by construction, invisible
 *     to GA4 — so the outage cannot show up in analytics.
 *
 * A `.catch()` alone is NOT sufficient, which is why nothing is returned here.
 * The library wires only `load` and `error` handlers and applies no timeout, so
 * a DNS blackhole that silently drops the request (rather than refusing it)
 * leaves the promise pending forever. A returned-but-caught promise would still
 * be awaited, and bootstrap would hang instead of failing — the same dead page,
 * harder to diagnose. Returning `void` removes the rejection path AND the hang
 * path together.
 *
 * Nothing is lost by not awaiting. `AnalyticsService.push()` writes straight to
 * `window.dataLayer` and never needs `gtm.js` to be loaded; GTM drains whatever
 * is already queued when it eventually arrives. The `dataLayer` array itself is
 * created synchronously by `addGtmToDom()` before it ever touches the network.
 *
 * Failure is logged exactly once, at `warn`, for engineers. There is
 * deliberately no user-facing error and no retry: the library leaves its
 * `#GTMscript` element in the DOM on failure, so a retry would inject a
 * duplicate element id, and telling visitors their blocker is interfering is
 * both pointless and hostile.
 */
export function loadGtmAtBootstrap(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

  // The `try` guards a synchronous throw and the `.catch` an asynchronous
  // rejection. `addGtmToDom()` builds its whole body inside a Promise executor,
  // so today every internal failure — including the "GTM id not provided" throw
  // for a misconfigured `environment.gtm.id` — arrives as a rejection. The
  // `try` is defence in depth: `inject()` can throw, and the library is free to
  // change. Neither path may escape this function.
  //
  // The `inject()` call is INSIDE the `try` for that reason. Resolving
  // `GoogleTagManagerService` is itself a failure path — a missing or misordered
  // provider throws NG0201 — and an initializer that throws stops Angular
  // bootstrapping outright, which is the exact dead-page failure this ticket
  // exists to remove. Leaving the lookup outside would have let the one error the
  // comment names escape the guard that names it. Matches
  // `startPageViewTracking`, which resolves its service inside the guard too.
  try {
    void inject(GoogleTagManagerService).addGtmToDom().catch(warnGtmUnavailable);
  } catch (error) {
    warnGtmUnavailable(error);
  }
}

/**
 * Records that analytics is unavailable, once, without involving the user.
 *
 * `addGtmToDom()` rejects with the bare value `false` rather than an `Error`,
 * so the reason is logged as-is rather than assuming a `message`.
 */
function warnGtmUnavailable(reason: unknown): void {
  // eslint-disable-next-line no-console
  console.warn(
    '[Analytics] Google Tag Manager could not be loaded — it is most likely blocked by a content blocker, DNS filter, or network policy. Analytics is degraded; the application is unaffected.',
    reason,
  );
}

/**
 * Starts the global `page_view` pipeline at application startup, independently
 * of any component.
 *
 * `PageViewService.init()` wires a single router subscription that emits exactly
 * one `page_view` per navigation, so it must run for EVERY route — including
 * routes whose components never touch analytics. Driving it from a bootstrap
 * app initializer (rather than a component) is what guarantees that coverage.
 *
 * Browser-only: `init()` is itself browser-only and idempotent (it no-ops under
 * SSR and on any second call). The guard here is belt-and-braces — it also lets
 * us skip the `inject(PageViewService)` entirely when not in the browser.
 *
 * Returns nothing and cannot hang: `init()` only wires a router subscription.
 * The `try` is there because a synchronous throw inside an app initializer also
 * stops Angular bootstrapping (see the invariant in `app.config.ts`), and no
 * analytics concern is ever worth taking the site down for (BBS-0070).
 */
export function startPageViewTracking(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

  try {
    inject(PageViewService).init();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Analytics] page_view tracking could not be started. The application is unaffected.', error);
  }
}

export const provideAnalytics = (config: GoogleTagManagerConfig) => {
  return makeEnvironmentProviders([
    provideGoogleTagManager(config),
    AnalyticsService,
    PageViewService,
    provideAppInitializer(loadGtmAtBootstrap),
    provideAppInitializer(startPageViewTracking),
  ]);
};
